import React, { useRef, useState } from "react";
import styled from "styled-components";
import { COLORS } from "../../assets/utils/enums/colors";
import { SubHeadline } from "../typography";
import ErrorBlock from "./error-text";
import breakpoints from "../../assets/styles/breakpoint";

interface EnterCodeProps {
  callback?: (code: string) => void;
  isLoading?: boolean;
  errorText?: string;
}

const EnterCode: React.FC<EnterCodeProps> = ({
  callback,
  isLoading,
  errorText,
}) => {
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const [code, setCode] = useState<string[]>(["", "", "", ""]);

  const checkAndSubmit = (newCode: string[]) => {
    if (newCode.every((char) => char.trim() !== "")) {
      callback?.(newCode.join(""));
    }
  };

  const handleInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!value) return;

    const newCode = [...code];
    newCode[index] = value[0];
    setCode(newCode);

    if (index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    checkAndSubmit(newCode);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      const newCode = [...code];

      if (code[index]) {
        newCode[index] = "";
        setCode(newCode);
      } else if (index > 0) {
        newCode[index - 1] = "";
        setCode(newCode);
        inputRefs.current[index - 1]?.focus();
      }
      callback?.(newCode.join(""));
    }

  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .toUpperCase()
      .replace(/^\d{4}$/, "");
    if (!pasted) return;

    const newCode = pasted.slice(0, 4).split("");
    setCode(newCode);
    checkAndSubmit(newCode);
  };

  return (
    <CodeInput>
      <div>
        {code.map((char, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el as HTMLInputElement;
            }}
            type="text"
            pattern="\d*"
            inputMode="numeric"
            value={char}
            onInput={(e) => {
              const target = e.target as HTMLInputElement;
              target.value = target.value.replace(/\D/g, "");
            }}
            onChange={(e) => handleInput(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            maxLength={1}
            disabled={isLoading}
            autoFocus={index === 0}
          />
        ))}
      </div>
      {errorText && <ErrorBlock errorText={errorText!} />}
    </CodeInput>
  );
};

export default EnterCode;

const CodeInput = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-bottom: 20px;
  div {
    display: flex;
    align-items: center;
    gap: 24px;
    input {
      width: 70px;
      height: 70px;
      border-radius: 12px;
      border: 1px solid ${COLORS.gray};
      background-color: ${COLORS.gray};
      outline: 1px solid transparent;
      caret-color: transparent;
      transition: outline-color 100ms cubic-bezier(0.25, 0.1, 0.25, 1),
        outline-width 100ms cubic-bezier(0.25, 0.1, 0.25, 1);
      text-align: center;
      ${SubHeadline.componentStyle.rules.join("")}

      &:focus {
        outline-color: ${COLORS["bg-text-color"]};
        outline-width: 2px;
      }

    }
    ${breakpoints.sm} {
      input {
        width: 60px;
        height: 60px;
      }
      gap: 20px;
    }
  }
`;
